/**
 * VCAModule — voltage-controlled amplifier (here, a Web Audio GainNode).
 *
 * Baseline gain defaults to 0. The expected wiring is that an Envelope's
 * modOutput is `.connect()`ed to `vca.modIn('gain')`, i.e. the underlying
 * GainNode's `.gain` AudioParam. AudioParam connections sum with the base
 * value, so the envelope's 0→1 signal drives amplitude while `gain` stays at
 * zero. Raise `gain` manually only if you want a drone without an envelope.
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

export class VCAModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'vca' as const;
  readonly displayName = 'VCA';
  readonly id: string;
  bypass = false;

  readonly gain = new NumericParameter({
    name: 'Gain',
    default: 0,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 15,
    // Bias high — a dice-rolled VCA gain of 0.05 makes the whole patch silent.
    randomSkew: 3,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });

  readonly parameters: readonly AnyParameter[] = [this.gain];

  private gainNode: GainNode | null = null;
  private started = false;

  constructor(id?: string) {
    this.id = id ?? mintId('vca');
  }

  get input(): AudioNode | null {
    return this.gainNode;
  }
  get output(): AudioNode | null {
    return this.gainNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    const ctx = engine.context;
    this.gainNode = ctx.createGain();
    this.gainNode.gain.value = this.gain.value;
    this.gain.bindAudioParam(this.gainNode.gain, ctx);
    this.started = true;
  }

  /**
   * Named modulation input. The VCA only has one ("gain") — aliased to the
   * default. Returns null for unknown names.
   */
  modIn(name = 'gain'): AudioParam | null {
    if (name !== 'gain') return null;
    return this.gainNode?.gain ?? null;
  }

  dispose(): void {
    this.gainNode?.disconnect();
    this.gainNode = null;
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }
}
